import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgIssuePeople } from '../../../lib/api/org-issues.js';
import type { PostView } from '../../../lib/api/team-meetings.js';
import { personLabel } from '../../../lib/org-issues.js';
import { stickyColorOf, stickyExcerpt, type StickyColor } from '../../../lib/team-meeting-board.js';

/**
 * 付箋の地の色（FigJam の付箋の色）。ボードはテーマに関わらず紙の色なので、文字は常に濃い色にする。
 * @graph-connects none
 */
export const STICKY_BG: Record<StickyColor, string> = {
  blue: '#b5dcff',
  green: '#b6ebc2',
  yellow: '#ffe597',
  orange: '#ffcfa3',
  red: '#ffb9ad',
  pink: '#ffc6ea',
  violet: '#d9c8ff',
  teal: '#aeeee6',
};

/** 付箋の形・影・文字の色（ボードの付箋と開いた付箋で揃える）。 @graph-connects none */
export const STICKY_CLASS =
  'flex flex-col rounded-[3px] p-3 text-left leading-snug text-[#1e1e1e] shadow-[0_1px_2px_rgba(0,0,0,0.12),0_4px_10px_rgba(0,0,0,0.08)]';

/** ボードに貼る付箋の大きさ（貼った付箋・書きかけの付箋・「付箋を貼る」で揃える）。 @graph-connects none */
export const STICKY_SIZE = 'w-full min-h-[148px] text-[13px]';

/**
 * 投稿者の色の付箋の地。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードで、投稿者ごとに決まる色を付箋の背景色のスタイルとして返し、同じ人の付箋の色を揃える
 * @graph-connects stickyColorOf [calls] 投稿者の色
 */
export function stickyStyle(authorId: string): CSSProperties {
  return { backgroundColor: STICKY_BG[stickyColorOf(authorId)] };
}

/**
 * ボードに貼る付箋 1 枚。本文の頭・投稿者・いいね数・議論メモの有無だけを出し、押すと開く
 * （操作は開いた先に置く）。棚卸し済みなら決めたネクストアクションを判子のように出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードに投稿を投稿者の色の付箋で貼り、押すと開いて操作できるようにし、棚卸し済みなら決めたネクストアクションを出す
 * @graph-connects stickyColorOf [calls] 投稿者の色
 * @graph-connects stickyExcerpt [calls] 本文の頭
 */
export function StickyNote({
  view,
  people,
  onOpen,
  className = '',
}: {
  view: PostView;
  people: OrgIssuePeople;
  onOpen: () => void;
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  const { post } = view;
  return (
    <button
      type="button"
      id={`post-${post.id}`}
      className={`${STICKY_CLASS} ${STICKY_SIZE} relative cursor-pointer transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b7fb5] ${className}`}
      style={stickyStyle(post.authorId)}
      onClick={onOpen}
    >
      {post.triage !== null && (
        <span className="absolute -right-2 -top-2 rotate-6 rounded border-2 border-[#d0453a] bg-white/80 px-1.5 py-0.5 text-[11px] font-bold text-[#d0453a]">
          {t(`teamMeetings.triage.result.${post.triage.nextAction.kind}`)}
        </span>
      )}
      <span
        className={`flex-1 whitespace-pre-wrap break-words ${post.triage === null ? '' : 'pt-3'}`}
      >
        {stickyExcerpt(post.body)}
      </span>
      <span className="mt-2 flex items-center justify-between gap-1 text-[11px] text-[#1e1e1e]/60">
        <span className="truncate">{personLabel(people, post.authorId)}</span>
        <span className="shrink-0 space-x-1">
          {post.likedBy.length > 0 && <span>👍{post.likedBy.length}</span>}
          {post.discussionNote !== '' && (
            <span aria-label={t('teamMeetings.post.hasNote')} role="img">
              💬
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
